/**
 * EvidenceChainVisualizer
 *
 * Vertical trace-chain component showing how a single SDLC action becomes
 * audit-grade evidence. Think "blockchain explorer for compliance events."
 *
 * 5 stages connected by dashed vertical lines with numbered circular nodes:
 *   1. SDLC Event       - type, repo, author, timestamp
 *   2. Policy Evaluation - list of policies with pass/fail/warn
 *   3. Control Mapping   - list of controls (SOC2, ISO27001, etc.)
 *   4. Evidence Record   - ID, hash, timestamp, integrity indicators
 *   5. Compliance Outcome - Control Status: VERIFIED / PARTIAL / FAILED
 *
 * Pure Fluent UI v9 + CSS. No external charting libraries.
 */
import React, { useMemo } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Card,
  Badge,
  Body1,
  Body2,
  Caption1,
} from '@fluentui/react-components';
import { semanticColors, categoryColors } from '../../theme/solution-theme';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ChainEvent {
  id: string;
  type: string;
  repository?: string;
  author?: string;
  timestamp: string;
}

interface PolicyEvaluation {
  policyId: string;
  policyName: string;
  result: 'pass' | 'fail' | 'warn';
}

interface ControlMapping {
  controlId: string;
  framework: string;
}

interface EvidenceRecord {
  id: string;
  hash: string;
  timestamp: string;
  hashVerified: boolean;
  timestampVerified: boolean;
  collectorVerified: boolean;
  confidence: number;
}

interface ChainOutcome {
  status: 'verified' | 'partial' | 'failed';
}

interface ChainData {
  event: ChainEvent;
  evaluations: PolicyEvaluation[];
  controlMappings: ControlMapping[];
  evidenceRecord: EvidenceRecord;
  outcome: ChainOutcome;
}

export interface EvidenceChainVisualizerProps {
  chain?: ChainData;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const COLOR_GREEN = semanticColors.success;
// COLOR_RED / COLOR_AMBER / SYSTEM_BLUE are not in brand-config.json's palette —
// no token represents them, so they are left as raw literals.
const COLOR_RED = '#EF4444';
const COLOR_AMBER = '#F59E0B';
const SYSTEM_BLUE = '#0078D4';

const RESULT_COLORS: Record<PolicyEvaluation['result'], string> = {
  pass: COLOR_GREEN,
  fail: COLOR_RED,
  warn: COLOR_AMBER,
};

const OUTCOME_COLORS: Record<ChainOutcome['status'], string> = {
  verified: COLOR_GREEN,
  partial: COLOR_AMBER,
  failed: COLOR_RED,
};

const OUTCOME_LABELS: Record<ChainOutcome['status'], string> = {
  verified: 'VERIFIED',
  partial: 'PARTIAL',
  failed: 'FAILED',
};

/** Default mock data: GitHub PR #291 on payments-api */
const MOCK_CHAIN: ChainData = {
  event: {
    id: 'evt-pr-291',
    type: 'pull_request',
    repository: 'payments-api',
    author: 'sarah.chen',
    timestamp: new Date(Date.now() - 12 * 60_000).toISOString(),
  },
  evaluations: [
    { policyId: 'POL-001', policyName: 'secret_scan', result: 'pass' },
    { policyId: 'POL-007', policyName: 'terraform_guardrails', result: 'pass' },
  ],
  controlMappings: [
    { controlId: 'CC6.1', framework: 'SOC2' },
    { controlId: 'A.12.4.1', framework: 'ISO27001' },
    { controlId: 'CM-3', framework: 'NIST 800-53' },
  ],
  evidenceRecord: {
    id: 'EVD-2026-0291',
    hash: 'sha256:a4f8c1...e7d3b2',
    timestamp: new Date(Date.now() - 11 * 60_000).toISOString(),
    hashVerified: true,
    timestampVerified: true,
    collectorVerified: true,
    confidence: 97,
  },
  outcome: {
    status: 'verified',
  },
};

/* ------------------------------------------------------------------ */
/*  Stage definitions                                                  */
/* ------------------------------------------------------------------ */

interface StageDef {
  number: number;
  label: string;
  accentColor: string;
}

const STAGES: StageDef[] = [
  { number: 1, label: 'SDLC Event', accentColor: categoryColors.code },        // Code magenta
  { number: 2, label: 'Policy Evaluation', accentColor: categoryColors.ai },   // AI purple
  { number: 3, label: 'Control Mapping', accentColor: categoryColors.compliance }, // Compliance cyan
  { number: 4, label: 'Evidence Record', accentColor: SYSTEM_BLUE },
  { number: 5, label: 'Compliance Outcome', accentColor: categoryColors.environment }, // Environment orange
];

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalNone,
    position: 'relative',
  },

  /* --- Stage row: node + content side by side --- */
  stageRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalL,
    position: 'relative',
  },

  /* --- Left rail: numbered circle + dashed connector --- */
  rail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
    width: '40px',
    position: 'relative',
  },

  nodeCircle: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForegroundStaticInverted,
    flexShrink: 0,
    zIndex: 2,
    position: 'relative',
    boxShadow: tokens.shadow4,
  },

  connector: {
    width: '2px',
    flexGrow: 1,
    minHeight: '16px',
    backgroundImage: `repeating-linear-gradient(
      to bottom,
      ${tokens.colorNeutralStroke2} 0px,
      ${tokens.colorNeutralStroke2} 6px,
      transparent 6px,
      transparent 12px
    )`,
    zIndex: 1,
  },

  connectorActive: {
    backgroundImage: `repeating-linear-gradient(
      to bottom,
      ${COLOR_GREEN} 0px,
      ${COLOR_GREEN} 6px,
      transparent 6px,
      transparent 12px
    )`,
  },

  /* --- Right content card --- */
  stageContent: {
    flex: '1 1 auto',
    paddingBottom: tokens.spacingVerticalL,
    minWidth: 0,
  },

  stageCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow2,
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    transitionProperty: 'box-shadow',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      boxShadow: tokens.shadow8,
    },
  },

  stageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },

  stageLabel: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },

  /* --- Detail rows inside stage cards --- */
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingLeft: tokens.spacingHorizontalS,
  },

  detailLabel: {
    color: tokens.colorNeutralForeground3,
    minWidth: '72px',
    flexShrink: 0,
  },

  detailValue: {
    color: tokens.colorNeutralForeground1,
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    wordBreak: 'break-all',
  },

  /* --- Policy list --- */
  policyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
  },

  policyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },

  policyDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },

  policyName: {
    color: tokens.colorNeutralForeground1,
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontSize: tokens.fontSizeBase200,
  },

  policyResult: {
    fontWeight: 600,
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
  },

  /* --- Control chips --- */
  controlChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    paddingLeft: tokens.spacingHorizontalS,
  },

  controlChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
  },

  controlFramework: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    fontFamily: tokens.fontFamilyBase,
  },

  /* --- Evidence integrity row --- */
  integrityRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    paddingLeft: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXS,
  },

  integrityIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase200,
    fontWeight: 600,
  },

  confidenceBar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXS,
  },

  confidenceTrack: {
    height: '6px',
    borderRadius: '3px',
    backgroundColor: tokens.colorNeutralBackground5,
    flex: '1 1 auto',
    maxWidth: '180px',
    overflow: 'hidden',
  },

  confidenceFill: {
    height: '100%',
    borderRadius: '3px',
    transitionProperty: 'width',
    transitionDuration: tokens.durationNormal,
  },

  confidenceLabel: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontVariantNumeric: 'tabular-nums',
    fontSize: tokens.fontSizeBase200,
    fontWeight: 700,
  },

  /* --- Outcome banner --- */
  outcomeBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingLeft: tokens.spacingHorizontalS,
  },

  outcomeStatus: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontVariantNumeric: 'tabular-nums',
    fontSize: tokens.fontSizeBase500,
    fontWeight: 700,
    letterSpacing: '0.05em',
  },
});

/* ------------------------------------------------------------------ */
/*  Utility                                                            */
/* ------------------------------------------------------------------ */

function formatTimestamp(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  return date.toLocaleString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/* ------------------------------------------------------------------ */
/*  Stage content renderers                                            */
/* ------------------------------------------------------------------ */

const SdlcEventContent: React.FC<{ event: ChainEvent }> = ({ event }) => {
  const styles = useStyles();
  return (
    <>
      <div className={styles.detailRow}>
        <Caption1 className={styles.detailLabel}>Type</Caption1>
        <Body2 className={styles.detailValue}>{event.type}</Body2>
      </div>
      <div className={styles.detailRow}>
        <Caption1 className={styles.detailLabel}>ID</Caption1>
        <Body2 className={styles.detailValue}>{event.id}</Body2>
      </div>
      {event.repository && (
        <div className={styles.detailRow}>
          <Caption1 className={styles.detailLabel}>Repo</Caption1>
          <Body2 className={styles.detailValue}>{event.repository}</Body2>
        </div>
      )}
      {event.author && (
        <div className={styles.detailRow}>
          <Caption1 className={styles.detailLabel}>Author</Caption1>
          <Body2 className={styles.detailValue}>{event.author}</Body2>
        </div>
      )}
      <div className={styles.detailRow}>
        <Caption1 className={styles.detailLabel}>Time</Caption1>
        <Body2 className={styles.detailValue}>{formatTimestamp(event.timestamp)}</Body2>
      </div>
    </>
  );
};

const PolicyEvaluationContent: React.FC<{ evaluations: PolicyEvaluation[] }> = ({ evaluations }) => {
  const styles = useStyles();
  return (
    <div className={styles.policyList}>
      {evaluations.map((ev) => (
        <div key={ev.policyId} className={styles.policyRow}>
          <span
            className={styles.policyDot}
            style={{ backgroundColor: RESULT_COLORS[ev.result] }}
            aria-hidden="true"
          />
          <Caption1 className={styles.policyName}>{ev.policyName}</Caption1>
          <Caption1
            className={styles.policyResult}
            style={{ color: RESULT_COLORS[ev.result] }}
          >
            {ev.result}
          </Caption1>
          <Caption1 style={{ color: tokens.colorNeutralForeground4 }}>
            {ev.policyId}
          </Caption1>
        </div>
      ))}
    </div>
  );
};

const ControlMappingContent: React.FC<{ mappings: ControlMapping[] }> = ({ mappings }) => {
  const styles = useStyles();
  return (
    <div className={styles.controlChips}>
      {mappings.map((m) => (
        <span key={`${m.framework}-${m.controlId}`} className={styles.controlChip}>
          {m.controlId}
          <span className={styles.controlFramework}>{m.framework}</span>
        </span>
      ))}
    </div>
  );
};

const EvidenceRecordContent: React.FC<{ record: EvidenceRecord }> = ({ record }) => {
  const styles = useStyles();

  const indicators: Array<{ label: string; verified: boolean }> = [
    { label: 'Hash', verified: record.hashVerified },
    { label: 'Timestamp', verified: record.timestampVerified },
    { label: 'Collector', verified: record.collectorVerified },
  ];

  const confidenceColor =
    record.confidence >= 90 ? COLOR_GREEN :
    record.confidence >= 70 ? COLOR_AMBER :
    COLOR_RED;

  return (
    <>
      <div className={styles.detailRow}>
        <Caption1 className={styles.detailLabel}>ID</Caption1>
        <Body2 className={styles.detailValue}>{record.id}</Body2>
      </div>
      <div className={styles.detailRow}>
        <Caption1 className={styles.detailLabel}>Hash</Caption1>
        <Body2 className={styles.detailValue}>{record.hash}</Body2>
      </div>
      <div className={styles.detailRow}>
        <Caption1 className={styles.detailLabel}>Time</Caption1>
        <Body2 className={styles.detailValue}>{formatTimestamp(record.timestamp)}</Body2>
      </div>

      {/* Integrity indicators */}
      <div className={styles.integrityRow}>
        {indicators.map((ind) => (
          <span
            key={ind.label}
            className={styles.integrityIndicator}
            style={{ color: ind.verified ? COLOR_GREEN : COLOR_RED }}
            aria-label={`${ind.label}: ${ind.verified ? 'verified' : 'failed'}`}
          >
            {ind.verified ? '\u2713' : '\u2715'} {ind.label}
          </span>
        ))}
      </div>

      {/* Confidence bar */}
      <div className={styles.confidenceBar}>
        <Caption1 className={styles.detailLabel}>Confidence</Caption1>
        <div className={styles.confidenceTrack}>
          <div
            className={styles.confidenceFill}
            style={{
              width: `${record.confidence}%`,
              backgroundColor: confidenceColor,
            }}
          />
        </div>
        <span
          className={styles.confidenceLabel}
          style={{ color: confidenceColor }}
        >
          {record.confidence}%
        </span>
      </div>
    </>
  );
};

const ComplianceOutcomeContent: React.FC<{ outcome: ChainOutcome }> = ({ outcome }) => {
  const styles = useStyles();
  const color = OUTCOME_COLORS[outcome.status];
  const label = OUTCOME_LABELS[outcome.status];

  return (
    <div className={styles.outcomeBanner}>
      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
        Control Status:
      </Caption1>
      <Badge
        appearance="filled"
        color={
          outcome.status === 'verified' ? 'success' :
          outcome.status === 'failed' ? 'danger' :
          'warning'
        }
        size="medium"
      >
        {label}
      </Badge>
      <span className={styles.outcomeStatus} style={{ color }}>
        {label}
      </span>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export const EvidenceChainVisualizer: React.FC<EvidenceChainVisualizerProps> = ({
  chain,
}) => {
  const styles = useStyles();
  const data = useMemo(() => chain ?? MOCK_CHAIN, [chain]);

  /** Determine whether the connector between stages should be green. */
  const isConnectorActive = (stageIndex: number): boolean => {
    // All stages before and including current must have succeeded for
    // the connector below them to be "active" (green).
    if (stageIndex >= 4) return false; // no connector after last stage

    const allPassed = data.evaluations.every((e) => e.result !== 'fail');
    const isVerified = data.outcome.status === 'verified';

    // If everything passed, all connectors are active
    if (allPassed && isVerified) return true;

    // Otherwise, only stages 0-1 connector is active (event always flows
    // to evaluation), and subsequent connectors depend on evaluation results
    if (stageIndex === 0) return true; // event always triggers evaluation
    if (stageIndex === 1) return allPassed;
    if (stageIndex === 2) return allPassed;
    if (stageIndex === 3) return allPassed && isVerified;

    return false;
  };

  /** Map stage index to its rendered content. */
  const renderStageContent = (stageIndex: number): React.ReactNode => {
    switch (stageIndex) {
      case 0:
        return <SdlcEventContent event={data.event} />;
      case 1:
        return <PolicyEvaluationContent evaluations={data.evaluations} />;
      case 2:
        return <ControlMappingContent mappings={data.controlMappings} />;
      case 3:
        return <EvidenceRecordContent record={data.evidenceRecord} />;
      case 4:
        return <ComplianceOutcomeContent outcome={data.outcome} />;
      default:
        return null;
    }
  };

  return (
    <Card>
      <div
        className={styles.root}
        role="list"
        aria-label="Evidence chain trace"
      >
        {STAGES.map((stage, idx) => {
          const isLast = idx === STAGES.length - 1;

          return (
            <div
              key={stage.number}
              className={styles.stageRow}
              role="listitem"
              aria-label={`Stage ${stage.number}: ${stage.label}`}
            >
              {/* Left rail — numbered circle + dashed connector */}
              <div className={styles.rail}>
                <div
                  className={styles.nodeCircle}
                  style={{ backgroundColor: stage.accentColor }}
                  aria-hidden="true"
                >
                  {stage.number}
                </div>
                {!isLast && (
                  <div
                    className={mergeClasses(
                      styles.connector,
                      isConnectorActive(idx) && styles.connectorActive,
                    )}
                    aria-hidden="true"
                  />
                )}
              </div>

              {/* Right content */}
              <div className={styles.stageContent}>
                <div className={styles.stageCard}>
                  <div className={styles.stageHeader}>
                    <Body1 className={styles.stageLabel}>{stage.label}</Body1>
                  </div>
                  {renderStageContent(idx)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
